import React from "react";

import { not } from "@/lib/not";
import { EventMap, EventT } from "@/types/event-types";

const eventListener = new Map();

export const useEventSubscriber = <K extends keyof EventMap>(
  event: K,
  callback: (...args: EventMap[K]) => void,
) => {
  // Always invoke the latest render's callback without resubscribing on
  // every render.
  const callbackRef = React.useRef(callback);
  callbackRef.current = callback;

  // The registered listener, so unsubscribe() can remove it.
  const listenerRef = React.useRef<((...args: EventMap[K]) => void) | null>(null);

  // Subscribe in an effect, not during render: StrictMode runs
  // mount -> cleanup -> mount, and a render-time registration torn down by
  // the cleanup never comes back, leaving a mounted component without its
  // subscription (events then silently go nowhere).
  React.useEffect(() => {
    const listener = (...args: EventMap[K]) => callbackRef.current(...args);
    listenerRef.current = listener;
    if (not(eventListener.has(event))) eventListener.set(event, new Set());
    eventListener.get(event).add(listener);
    return () => {
      eventListener.get(event)?.delete(listener);
      listenerRef.current = null;
    };
  }, [event]);

  return {
    unsubscribe: () => {
      if (listenerRef.current) eventListener.get(event)?.delete(listenerRef.current);
      listenerRef.current = null;
    },
  };
};

const publisher = (event: string, payload?: unknown) => {
  if (eventListener.get(event) != undefined) {
    eventListener.get(event).forEach((cb: (arg0: unknown) => void) => {
      cb(payload);
    });
  }
};

export const useEventPublisher = () => {
  return publisher;
};

export const emit = <K extends keyof EventMap>(...args: EventT<K>) => {
  publisher(...args);
};
